import React from 'react';
import styles from './style.module.scss';
import { buildClassName } from 'utils/build-class-name';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { ClientPreferences } from 'data/client-info-pages/types';

export interface WalkabilityInfoProps {
  /** The name of the area for display */
  areaName?: string;
  /** Client preferences for walkability, transit, commute, etc. */
  clientPreferences?: ClientPreferences;
}

const importanceKeys: Record<number, { path: typeof LOCALE_COMMON_PATHS[keyof typeof LOCALE_COMMON_PATHS]; defaultValue: string }> = {
  1: { path: LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_IMPORTANCENOTIMPORTANT, defaultValue: 'Not Important' },
  2: { path: LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_IMPORTANCESOMEWHATIMPORTANT, defaultValue: 'Somewhat Important' },
  3: { path: LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_IMPORTANCEMODERATE, defaultValue: 'Moderate' },
  4: { path: LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_IMPORTANCEIMPORTANT, defaultValue: 'Important' },
  5: { path: LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_IMPORTANCEVERYIMPORTANT, defaultValue: 'Very Important' },
};

const ImportanceDots = ({ level }: { level: number }) => {
  const { t } = useTranslation(Namespaces.COMMON);

  return (
    <div className={styles.importanceBar}>
      {[1, 2, 3, 4, 5].map(i => (
        <div
          key={i}
          className={buildClassName(styles.dot, i <= level ? styles.dotActive : undefined)}
        />
      ))}
      <span className={styles.importanceLabel}>
        {importanceKeys[level] ? t(importanceKeys[level].path, { defaultValue: importanceKeys[level].defaultValue }) : ''}
      </span>
    </div>
  );
};

/**
 * Displays walkability, transit, and transportation preferences for the client.
 * Built from client preferences data — no external API calls required.
 */
const WalkabilityInfo = ({ areaName, clientPreferences }: WalkabilityInfoProps) => {
  const { t } = useTranslation(Namespaces.COMMON);

  if (!clientPreferences) {
    return null;
  }

  const {
    walkability_preference,
    public_transit_importance,
    drives,
    work_address,
    max_commute_minutes,
    pet_friendly,
    accessibility_needs,
    parking_required,
  } = clientPreferences;

  const hasContent =
    walkability_preference !== undefined ||
    public_transit_importance !== undefined ||
    drives !== undefined ||
    pet_friendly ||
    accessibility_needs ||
    parking_required;

  if (!hasContent) {
    return null;
  }

  return (
    <section className={styles.component}>
      <h2 className={styles.heading}>
        {areaName
          ? t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_HEADINGWITHAREA, { areaName, defaultValue: 'Walkability & Transportation — {{areaName}}' })
          : t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_HEADING, { defaultValue: 'Walkability & Transportation' })}
      </h2>
      <p className={styles.description}>
        {t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_DESCRIPTION, { defaultValue: 'Transportation and lifestyle preferences that matter for this search.' })}
      </p>

      <div className={styles.cardsGrid}>
        {/* Walkability */}
        {walkability_preference !== undefined && walkability_preference > 0 && (
          <div className={styles.card}>
            <div className={buildClassName(styles.cardIcon, styles.walkIcon)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="5" r="2" />
                <path d="M10 22l2-7 3 3v6" />
                <path d="M10 12l-2 4" />
                <path d="M14 12l2-2" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_WALKINGDISTANCETITLE, { defaultValue: 'Walking Distance' })}</h3>
            <ImportanceDots level={walkability_preference} />
            <p className={styles.cardText}>
              {walkability_preference >= 4
                ? t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_WALKINGDISTANCEHIGH, { defaultValue: 'Proximity to shops, groceries, and daily errands on foot is a high priority.' })
                : walkability_preference >= 3
                  ? t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_WALKINGDISTANCEMEDIUM, { defaultValue: 'Walking access to some amenities is preferred but not required.' })
                  : t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_WALKINGDISTANCELOW, { defaultValue: 'Walkability is nice to have but not a primary factor.' })}
            </p>
          </div>
        )}

        {/* Public Transit */}
        {public_transit_importance !== undefined && public_transit_importance > 0 && (
          <div className={styles.card}>
            <div className={buildClassName(styles.cardIcon, styles.transitIcon)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="4" y="3" width="16" height="16" rx="2" />
                <path d="M4 11h16" />
                <path d="M12 3v8" />
                <circle cx="8" cy="15" r="1" />
                <circle cx="16" cy="15" r="1" />
                <path d="M8 19l-2 3" />
                <path d="M16 19l2 3" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PUBLICTRANSITTITLE, { defaultValue: 'Public Transit' })}</h3>
            <ImportanceDots level={public_transit_importance} />
            <p className={styles.cardText}>
              {public_transit_importance >= 4
                ? t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PUBLICTRANSITHIGH, { defaultValue: 'Easy access to bus, subway, or train lines is essential for daily commuting.' })
                : public_transit_importance >= 3
                  ? t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PUBLICTRANSITMEDIUM, { defaultValue: 'Nearby transit options are preferred for flexibility.' })
                  : t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PUBLICTRANSITLOW, { defaultValue: 'Public transit access is a secondary consideration.' })}
            </p>
          </div>
        )}

        {/* Drives */}
        {drives !== undefined && (
          <div className={styles.card}>
            <div className={buildClassName(styles.cardIcon, styles.driveIcon)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="3" width="15" height="13" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>
              {drives
                ? t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_HASVEHICLETITLE, { defaultValue: 'Has a Vehicle' })
                : t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_NOVEHICLETITLE, { defaultValue: 'No Vehicle' })}
            </h3>
            <p className={styles.cardText}>
              {drives
                ? t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_DRIVESTEXT, { defaultValue: 'The client drives — properties with parking and highway/road access are suitable.' })
                : t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_NODRIVESTEXT, { defaultValue: 'The client does not drive — proximity to transit, walkable amenities, and cycling infrastructure is important.' })}
            </p>
          </div>
        )}

        {/* Parking */}
        {parking_required && (
          <div className={styles.card}>
            <div className={buildClassName(styles.cardIcon, styles.driveIcon)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="2" width="20" height="20" rx="2" />
                <path d="M9 17V7h4a3 3 0 0 1 0 6H9" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PARKINGREQUIREDTITLE, { defaultValue: 'Parking Required' })}</h3>
            <p className={styles.cardText}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PARKINGREQUIREDTEXT, { defaultValue: 'Properties should include dedicated parking — a garage, driveway, or assigned parking spot.' })}
            </p>
          </div>
        )}

        {/* Pet Friendly */}
        {pet_friendly && (
          <div className={styles.card}>
            <div className={buildClassName(styles.cardIcon, styles.petIcon)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 5.172C10 3.782 8.884 2.5 7.5 2.5S5 3.782 5 5.172c0 1.39.666 2.828 2.5 2.828S10 6.562 10 5.172z" />
                <path d="M19 5.172C19 3.782 17.884 2.5 16.5 2.5S14 3.782 14 5.172c0 1.39.666 2.828 2.5 2.828S19 6.562 19 5.172z" />
                <path d="M7 14c-1.5 0-3-1.5-3-3.5S5.5 7 7 7s3 1.5 3 3.5S8.5 14 7 14z" />
                <path d="M17 14c-1.5 0-3-1.5-3-3.5S15.5 7 17 7s3 1.5 3 3.5S18.5 14 17 14z" />
                <path d="M12 22c-4 0-7-2-7-5 0-1.5 1-3 2.5-3.5C9 13 10.5 12 12 12s3 1 4.5 1.5C18 14 19 15.5 19 17c0 3-3 5-7 5z" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PETFRIENDLYTITLE, { defaultValue: 'Pet Friendly' })}</h3>
            <p className={styles.cardText}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_PETFRIENDLYTEXT, { defaultValue: 'Looking for pet-friendly properties — nearby parks, walking trails, and pet-welcoming buildings preferred.' })}
            </p>
          </div>
        )}

        {/* Accessibility */}
        {accessibility_needs && (
          <div className={styles.card}>
            <div className={buildClassName(styles.cardIcon, styles.accessIcon)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="4" r="2" />
                <path d="M12 6v6" />
                <path d="M8 12h8" />
                <path d="M9 18l3-6 3 6" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <h3 className={styles.cardTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_ACCESSIBILITYTITLE, { defaultValue: 'Accessibility Needs' })}</h3>
            <p className={styles.cardText}>
              {t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_ACCESSIBILITYTEXT, { defaultValue: 'The client has accessibility requirements — single-level homes, elevators, and accessible entrances are preferred.' })}
            </p>
          </div>
        )}
      </div>

      {/* Commute Info */}
      {work_address && (
        <div className={styles.commuteInfo}>
          <div className={buildClassName(styles.cardIcon, styles.commuteIcon)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </div>
          <div className={styles.commuteContent}>
            <div className={styles.commuteTitle}>{t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_COMMUTEDESTINATIONTITLE, { defaultValue: 'Commute Destination' })}</div>
            <div className={styles.commuteAddress}>{work_address}</div>
            {max_commute_minutes && (
              <div className={styles.commuteMax}>
                {t(LOCALE_COMMON_PATHS.CITYINFO_WALKABILITYINFO_MAXCOMMUTE, { minutes: String(max_commute_minutes), defaultValue: 'Max commute: {{minutes}} minutes' })}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default WalkabilityInfo;
